// File: 'src/components/StatusBar/StatusBar.tsx'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	useAcknowledgeUpdatedUdfValuesMutation,
	useGetUpdateLeasedUdfValuesStatusQuery,
} from '../../api/asset.api.ts';
import {
	UPLOAD_FAILED_STATUS,
	UPLOAD_IN_PROGRESS_STATUS,
	UPLOAD_SUCCESSFUL_STATUS,
} from '../../constants/messages.tsx';
import { useUserAccountRole } from '../../hooks/useUserAccountRole';
import {
	acknowledged,
	bumpAssetGridRefresh,
	selectLeasedUdfInProgress,
	setUploadInProgress,
} from '../../redux/slices/udfUpdateSlice.ts';

const SUCCESS = 'SUCCESS';
const ERROR = 'ERROR';

export default function StatusBar() {
	const dispatch = useDispatch();

	const [acknowledgeUpdatedUdfValues] = useAcknowledgeUpdatedUdfValuesMutation();
	const leasedUdfInProgress = useSelector(selectLeasedUdfInProgress);
	const [polling, setPolling] = useState(false);
	const previousJobStatusRef = useRef<string | undefined>(undefined);

	// Get user role
	const { role } = useUserAccountRole();
	const userHasViewOnlyAccess = role?.permissionLevel === 'VIEW';

	// Only run the query if not read-only
	const { data: status } = useGetUpdateLeasedUdfValuesStatusQuery(
		{},
		{
			pollingInterval: polling ? 5000 : 0,
			skip: userHasViewOnlyAccess === true,
		}
	);

	useEffect(() => {
		const currentJobStatus = status?.jobStatus;
		if (currentJobStatus) {
			dispatch(setUploadInProgress());
			setPolling(currentJobStatus !== SUCCESS && currentJobStatus !== ERROR);
			if (previousJobStatusRef.current !== SUCCESS && currentJobStatus === SUCCESS) {
				dispatch(bumpAssetGridRefresh());
			}
			previousJobStatusRef.current = currentJobStatus;
		}
	}, [status?.jobStatus, dispatch]);

	// Memoized styles and computed icon must be declared before any early return
	const iconSx = useMemo(() => ({ color: (theme: any) => theme.palette.common.white }), []);
	const alertIcon = useMemo(() => {
		switch (status?.jobStatus) {
			case ERROR:
				return <ErrorIcon sx={iconSx} />;
			case SUCCESS:
				return <CheckCircleIcon sx={iconSx} />;
			default:
				return <InfoIcon sx={iconSx} />;
		}
	}, [status?.jobStatus, iconSx]);

	if (!leasedUdfInProgress) {
		return null;
	}

	const statusMessage = () => {
		if (status?.jobStatus === SUCCESS) {
			return UPLOAD_SUCCESSFUL_STATUS;
		} else if (status?.jobStatus === ERROR) {
			return UPLOAD_FAILED_STATUS;
		} else {
			return UPLOAD_IN_PROGRESS_STATUS;
		}
	};

	return (
		<Alert
			sx={{
				backgroundColor: (theme) =>
					status?.jobStatus === ERROR ? theme.palette.error.main : theme.palette.brand.primary,
				color: (theme) => theme.palette.common.white,
				width: '60%',
				minHeight: 50,
				display: 'flex',
				alignItems: 'flex-start',
				margin: '0 auto',
				px: 5,
				py: 1,
				'& .MuiAlert-action': {
					paddingTop: 0,
					marginTop: 0,
				},
			}}
			icon={alertIcon}
			action={
				(status?.jobStatus === SUCCESS || status?.jobStatus === ERROR) && (
					<Button
						color="inherit"
						size="medium"
						sx={{ px: 0, alignSelf: 'flex-start' }}
						onClick={() => {
							acknowledgeUpdatedUdfValues();
							dispatch(acknowledged());
						}}
					>
						Dismiss
					</Button>
				)
			}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'flex-end',
					width: '100%',
				}}
			>
				<Typography
					fontWeight="bold"
					paddingLeft={1}
					color="inherit"
					sx={{ letterSpacing: '0.02em' }}
				>
					{statusMessage()}
				</Typography>
			</Box>
		</Alert>
	);
}
